import { useAuthFetch } from "@p0security/frontend-core";
import { FirestoreDoc } from "@p0security/frontend-core/react";
import { PermissionRequest } from "@p0security/shared/types/permission";
import {
  ActiveRequestStatuses,
  PendingRequestStatuses,
} from "@p0security/shared/types/request-status";
import {
  Empty,
  Grid,
  List,
  Pagination,
  Spacing,
  Spin,
  Typography,
} from "@p0security/ui";
import { usePagination } from "hooks/usePagination";
import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router";

import { ActiveRequestActions } from "../components/ActiveRequestActions";
import { ApproveRequestModal } from "../components/ApproveRequestModal";
import { PendingRequestActions } from "../components/PendingRequestActions";
import { RequestDetailDrawer } from "../components/RequestDetailsDrawer";
import { RequestFilters } from "../components/RequestFilters";
import { RequestPreview } from "../components/RequestPreview";
import { StyledRequestControls } from "../components/StyledRequestControls";
import { useActiveAndPendingPagination } from "../hooks/useActiveAndPendingPagination";
import { useFilteredData } from "../hooks/useFilteredData";
import { usePermissionRequestFilterOptions } from "../hooks/useRequestFilterOptions";
import { useRequests } from "../hooks/useRequests";
import { handleRequestActionResponse } from "../requestUtils";
import { PERMISSION_REQUEST_FILTER_CONFIG } from "../utils/filterConfigs";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 10;

const ActiveRequestItem: React.FC<{
  item: FirestoreDoc<PermissionRequest>;
  handleRevoke: (id: string) => void;
  processing: boolean;
}> = ({ handleRevoke, item, processing }) => {
  const handleItem = useCallback(
    () => handleRevoke(item.id),
    [handleRevoke, item.id]
  );
  return (
    <List.Item
      actions={[
        <ActiveRequestActions
          key={item.id}
          request={item}
          onRevoke={handleItem}
          processing={processing}
        />,
      ]}
    >
      <RequestPreview requestId={item.id} request={item.data} />
    </List.Item>
  );
};

export const ActiveRequests: React.FC = () => {
  const { requestId } = useParams();
  const { sm } = Grid.useBreakpoint();

  const activeRequests = useRequests(ActiveRequestStatuses);
  const pendingRequests = useRequests(PendingRequestStatuses);

  // Combine all requests so all can be filtered together
  const allRequests = useMemo(
    () => [...activeRequests.requests, ...pendingRequests.requests],
    [activeRequests.requests, pendingRequests.requests]
  );

  const options = usePermissionRequestFilterOptions(allRequests);

  const { filters, applyFilters, resetFilters } = useFilteredData<
    FirestoreDoc<PermissionRequest>
  >(PERMISSION_REQUEST_FILTER_CONFIG);

  const [requestProcessing, setRequestProcessing] = useState(false);
  const [requestInModal, setRequestInModal] =
    useState<FirestoreDoc<PermissionRequest>>();
  const [pagination, _, { handlePageChange }] = usePagination();
  const authFetch = useAuthFetch(undefined, setRequestProcessing);

  const { page, pageSize } = useMemo(
    () => ({
      page: pagination.page ?? DEFAULT_PAGE,
      pageSize: pagination.pageSize ?? DEFAULT_PAGE_SIZE,
    }),
    [pagination]
  );

  const handleDeny = useCallback(
    async (requestId: string) => {
      handleRequestActionResponse(
        await authFetch(`permission-requests/${requestId}/deny`, {
          method: "POST",
        }),
        "Request denied"
      );
    },
    [authFetch]
  );

  const handleRevoke = useCallback(
    async (requestId: string) => {
      handleRequestActionResponse(
        await authFetch(`permission-requests/${requestId}/revoke`, {
          method: "POST",
        }),
        "Access revoked"
      );
    },
    [authFetch]
  );

  const renderActiveRequest = useCallback(
    (item: FirestoreDoc<PermissionRequest>) => (
      <ActiveRequestItem
        key={item.id}
        handleRevoke={handleRevoke}
        item={item}
        processing={requestProcessing}
      />
    ),
    [handleRevoke, requestProcessing]
  );

  const renderPendingRequest = useCallback(
    (item: FirestoreDoc<PermissionRequest>) => {
      return (
        <StyledRequestControls
          isDesktop={sm}
          actions={PendingRequestActions(
            () => setRequestInModal(item),
            () => handleDeny(item.id)
          )}
        >
          <RequestPreview requestId={item.id} request={item.data} />
        </StyledRequestControls>
      );
    },
    [handleDeny, sm]
  );

  const closeModal = useCallback(() => {
    setRequestInModal(undefined);
  }, []);

  // Apply all filters
  const filteredActiveRequests = useMemo(
    () => applyFilters(activeRequests.requests),
    [activeRequests.requests, applyFilters]
  );

  const filteredPendingRequests = useMemo(
    () => applyFilters(pendingRequests.requests),
    [pendingRequests.requests, applyFilters]
  );

  // Apply pagination to each section independently
  const {
    paginatedActiveSection: paginatedActiveRequests,
    paginatedPendingSection: paginatedPendingRequests,
    totalItems,
  } = useActiveAndPendingPagination(
    filteredActiveRequests,
    filteredPendingRequests,
    { page, pageSize }
  );

  const showAnyRequests = useMemo(
    () =>
      filteredActiveRequests.length > 0 || filteredPendingRequests.length > 0,
    [filteredActiveRequests, filteredPendingRequests]
  );

  return (
    <>
      <div style={{ maxWidth: 1500 }}>
        <RequestFilters
          filters={filters}
          options={options}
          hideApprovalDateFilter={false}
          hideExport={true}
          resetFilters={resetFilters}
        />
        <Typography.Title level={4}>Active</Typography.Title>
        <div style={{ minHeight: "22em" }}>
          {activeRequests.loading ? (
            <Spin />
          ) : showAnyRequests ? (
            paginatedActiveRequests.length > 0 ? (
              <List
                dataSource={paginatedActiveRequests}
                renderItem={renderActiveRequest}
                pagination={false}
              />
            ) : (
              <Empty description="No active requests on this page" />
            )
          ) : (
            <Empty description="No active requests" />
          )}
        </div>
        <Typography.Title level={4}>Pending</Typography.Title>
        <div style={{ minHeight: "22em" }}>
          {pendingRequests.loading ? (
            <Spin />
          ) : showAnyRequests ? (
            paginatedPendingRequests.length > 0 ? (
              <List
                dataSource={paginatedPendingRequests}
                renderItem={renderPendingRequest}
                pagination={false}
              />
            ) : (
              <Empty description="No pending requests on this page" />
            )
          ) : (
            <Empty description="No pending requests" />
          )}
        </div>
        {showAnyRequests && (
          <Spacing asChild mt={16}>
            <Pagination
              current={page}
              onChange={handlePageChange}
              pageSize={pageSize}
              showSizeChanger
              size="small"
              className="text-right"
              total={totalItems}
            />
          </Spacing>
        )}
      </div>
      <ApproveRequestModal
        request={requestInModal}
        loading={requestProcessing}
        onClose={closeModal}
      />
      {requestId && <RequestDetailDrawer requestId={requestId} />}
    </>
  );
};
